import { Controller } from "@hotwired/stimulus"

// This controller handles the star rating functionality for product reviews
// It manages star selection, visual feedback, and integrates with Alpine.js
//
// The stars are exposed to assistive technology as an ARIA radio group: the
// container is role="radiogroup" and each star is role="radio" with an
// aria-label ("1 star", "2 stars", ...). Keyboard users can tab to the group
// and use the arrow keys / Home / End / Space / Enter to set a rating, so the
// gate that unlocks the rest of the review form is fully keyboard accessible.
export default class extends Controller {
  static targets = ["star", "field", "form"]
  static values = {
    autoSubmit: { type: Boolean, default: false }
  }

  connect() {
    // Set initial visual + accessibility state based on existing rating value
    this.updateStars()
  }

  // Handle star click to set rating
  rate(event) {
    this.select(parseInt(event.currentTarget.dataset.value))
  }

  // Handle keyboard interaction following the ARIA radio group pattern.
  // currentTarget is the focused star, so its data-value is the focused rating.
  keydown(event) {
    const focusedValue = parseInt(event.currentTarget.dataset.value)
    const maxValue = this.starTargets.length
    const hasSelection = (parseInt(this.fieldTarget.value) || 0) > 0
    let nextValue

    switch (event.key) {
      case "ArrowRight":
      case "ArrowUp":
        // From an unselected group the first arrow selects the focused star,
        // afterwards it moves to the next star.
        nextValue = hasSelection ? Math.min(focusedValue + 1, maxValue) : focusedValue
        break
      case "ArrowLeft":
      case "ArrowDown":
        nextValue = Math.max(focusedValue - 1, 1)
        break
      case "Home":
        nextValue = 1
        break
      case "End":
        nextValue = maxValue
        break
      case "Enter":
      case " ":
        nextValue = focusedValue
        break
      default:
        return
    }

    // Prevent the page from scrolling and the form from submitting on Enter.
    event.preventDefault()
    this.select(nextValue)
    this.focusStar(nextValue)
  }

  // Set the rating value, sync Alpine.js, and refresh the visual + a11y state
  select(value) {
    this.fieldTarget.value = value

    // Update Alpine.js model if present (for form validation + tag visibility)
    if (this.fieldTarget._x_model) {
      this.fieldTarget._x_model.set(value)
    }

    this.fieldTarget.dispatchEvent(new Event("change", { bubbles: true }))

    this.updateStars()

    // Optionally auto-submit the form (for quick ratings)
    if (this.autoSubmitValue && this.hasFormTarget) {
      this.formTarget.submit()
    }
  }

  // Move keyboard focus to the star with the given value
  focusStar(value) {
    const star = this.starTargets.find(star => parseInt(star.dataset.value) === value)
    if (star) {
      star.focus()
    }
  }

  // Update the visual state, aria-checked, and roving tabindex of all stars
  // based on the current rating
  updateStars() {
    if (!this.hasFieldTarget) return

    const currentValue = parseInt(this.fieldTarget.value) || 0
    // The star that is reachable with Tab: the selected one, or the first star
    // when nothing is selected yet.
    const focusableValue = currentValue || 1

    this.starTargets.forEach(star => {
      const starValue = parseInt(star.dataset.value)

      // Visually fill every star up to and including the current rating
      star.classList.toggle("text-yellow-400", starValue <= currentValue)

      // Only the exactly-selected radio is checked
      star.setAttribute("aria-checked", starValue === currentValue ? "true" : "false")

      // Roving tabindex: exactly one star is in the tab order
      star.tabIndex = starValue === focusableValue ? 0 : -1
    })
  }
}
